import { useReportingTimeZone } from '@/hooks/use-reporting-timezone';
import { SupernovaDatePicker, civilDateToInstant, instantToCivilDate, layoutClassesOnly } from './date-picker';

interface DateInputProps {
  /** 'YYYY-MM-DD' (HTML date format — preserved so form/API logic is unaffected). */
  value?: string;
  /** Called with a synthetic `{ target: { value } }` so existing
   *  `(e) => setX(e.target.value)` handlers keep working unchanged. */
  onChange?: (e: { target: { value: string } }) => void;
  /** 'YYYY-MM-DD' lower / upper bounds (earlier/later days are disabled). */
  min?: string;
  max?: string;
  disabled?: boolean;
  className?: string;
  /** Render the calendar into `document.body` instead of inline. Needed inside
   *  anything that clips (an `overflow-x-auto` table wrapper also clips
   *  vertically), where the popup would otherwise be cut off. */
  portal?: boolean;
}

/**
 * DateInput — a date field built on the app-standard SupernovaDatePicker.
 *
 * Keeps the legacy string `value` + synthetic `onChange` API so every existing
 * consumer works unchanged, while gaining tenant/location timezone behaviour:
 * the day is displayed and selected in the active location zone (both the value
 * and min/max convert through the same zone, so nothing shifts by a day). The
 * stored value stays 'YYYY-MM-DD', so downstream form/API logic is untouched.
 */
export function DateInput({ value, onChange, min, max, disabled, className, portal }: DateInputProps) {
  const { timeZone } = useReportingTimeZone();
  return (
    <SupernovaDatePicker
      mode="date"
      timezone={timeZone}
      presets={false}
      disabled={disabled}
      portal={portal}
      // Legacy callers pass the old native-input skin; only its layout survives
      // (the picker draws its own bordered input — see layoutClassesOnly).
      className={layoutClassesOnly(className)}
      value={civilDateToInstant(value, timeZone)}
      minDate={civilDateToInstant(min, timeZone) ?? undefined}
      maxDate={civilDateToInstant(max, timeZone) ?? undefined}
      onChange={(v) => onChange?.({ target: { value: instantToCivilDate(v as Date | null, timeZone) } })}
    />
  );
}
